import React, { useState, useCallback, useMemo } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';

import { Button, Stack, StackItem,
  Toolbar, ToolbarContent, ToolbarItem, ToolbarGroup, Flex, FlexItem, FormGroup
} from '@patternfly/react-core';

import ContentPage from 'components/ContentView/ContentPage/ContentPage';
import useAlerts from 'utils/hooks/useAlerts';
import { useTranslation } from 'localization/useTranslation';
import ModelsContainer from 'components/Model/ModelsContainer';
import ModelType from 'components/Model/ModelType';
import createQueryRequest from 'services/createQueryRequest';
import createMutationRequest from 'utils/createMutationRequest';
import useAPI from 'services/useAPI';
import { privileges } from 'utils/types';
import BreadcrumbSkeleton from 'components/Breadcrumb/BreadcrumbSkeleton';
import T from 'localization/LocalizationKeys';
import { ResourceRelationType } from 'utils/types';
import { orgKeys } from './queries';
import { useOrgGroupRelations, useOrgUserRelations } from './Relations/queries';
import useBoolean from 'utils/hooks/useBoolean';
import { VisibleColumns } from 'components/RelationTable/RelationTable';
import ViewDefaultContactModal from './DefaultContactModal';


const createTenantPrivilages = () => {
  return {
    resourceType: 'environment',
    optionalResourceId: 'default',
    optionalSubjectFilter: {
      subjectType: 'tenant',
      optionalSubjectId: 'default'
    }
  };
};

/**
 * @typedef {object} ResourceType
 * @prop {string} id
 * @prop {string} displayName
 * @prop {string} type
 * @prop {string} relation
 */

/**
 * @type {(import('components/Common/SimpleTable/SimpleTable').ColumnType<ResourceType> & { columnKey: string })[]}
 */
const defaultTableColumns = [
  {
    columnKey: VisibleColumns.displayName,
    label: T.common.displayname,
    path: 'displayName',
    searchItem: true,
    isSortable: true
  },
  {
    columnKey: VisibleColumns.id,
    label: T.common.id,
    path: 'id',
    isSortable: true
  },
  {
    columnKey: VisibleColumns.type,
    label: T.common.type,
    path: 'type',
    isSortable: true
  }
];

const privilegeLabels = [
  { id: privileges.AssetManagement, label: T.privileges.asset_management },
  { id: privileges.DashboardManagement, label: T.privileges.dashboard_management },
  { id: privileges.UserManagement, label: T.privileges.user_management },
  { id: privileges.TreeManagement, label: T.privileges.tree_management },
  { id: privileges.ModelManagement, label: T.privileges.model_management },
  { id: privileges.AlarmManagement, label: T.privileges.alarm_management },
  { id: privileges.OrganizationManagement, label: T.privileges.org_management },
  { id: privileges.ProviderManagement, label: T.privileges.provider_management },
  { id: privileges.ProviderClientManagement, label: T.privileges.provider_client_management },
  { id: privileges.NotificationManagement, label: T.privileges.notification_management }
];

const defaultPrivileges = {
  [privileges.AssetManagement]: false,
  [privileges.DashboardManagement]: false,
  [privileges.UserManagement]: false,
  [privileges.TreeManagement]: false,
  [privileges.ModelManagement]: false,
  [privileges.AlarmManagement]: false,
  [privileges.OrganizationManagement]: false,
  [privileges.ProviderManagement]: false,
  [privileges.ProviderClientManagement]: false,
  [privileges.NotificationManagement]: false
};
// TODO translate
const noParent = { id: null, displayName: 'No Parent' };

const defaultOrganization = {
  id: '',
  parentOrganizationId: noParent,
  displayName: '',
  description: '',
  privileges: defaultPrivileges,
  settings: {
    needLicenseAccept: false
  }
};


export const OrgName = ({ orgId }) => {
  const api = useAPI();
  const orgQuery = useQuery(orgKeys.organization(orgId), createQueryRequest(api.core.getOrganization));
  return orgQuery.isLoading ? <BreadcrumbSkeleton /> : (orgQuery.data?.displayName ?? '');
};


// const parseParentId = (parentId) => {
//   if (parentId === 'null') {
//     return null;
//   }
//   return parentId;
// };


const relations = [
  {
    id: ResourceRelationType.Dashboards,
    displayName: T.sidebar.item.dashboards
  },
  {
    id: ResourceRelationType.Assets,
    displayName: T.sidebar.item.assets
  },
  {
    id: ResourceRelationType.Users,
    displayName: T.sidebar.item.users
  },
  {
    id: ResourceRelationType.Groups,
    displayName: T.sidebar.item.groups
  },
  {
    id: ResourceRelationType.Models,
    displayName: T.sidebar.item.models
  },
  {
    id: ResourceRelationType.Alarms,
    displayName: T.sidebar.item.alarms
  },
  {
    id: ResourceRelationType.Providers,
    displayName: T.sidebar.item.providers
  },
  {
    id: ResourceRelationType.Trees,
    displayName: T.sidebar.item.trees
  },
  {
    id: ResourceRelationType.Notifications,
    displayName: T.sidebar.item.notifications
  }
];


const RelationLinks = ({ relations }) => {
  const history = useHistory();
  const location = useLocation();
  const T = useTranslation();
  return (
    // TODO Translate
    <FormGroup label="Relations">
      <Stack hasGutter>
        { _.map(relations, (relation) => (
          <StackItem key={relation.id}>
            <Button
              variant="secondary"
              style={{ width: '100%' }}
              onClick={() => {
                history.push(`${location.pathname}/${relation.id}`);
              }}>
              { T.localize(relation.displayName) }
            </Button>
          </StackItem>
        )) }
      </Stack>
    </FormGroup>
  );
};


const Privileges = ({ availablePrivileges, privileges, setPrivileges }) => {
  const models = useMemo(() => {
    const availablePrivilegeLabels = _.filter(privilegeLabels, (priv) => _.includes(availablePrivileges, priv.id));
    return _.map(availablePrivilegeLabels, (priv) => ({
      type: ModelType.SWITCH,
      label: priv.label,
      path: `${priv.id}`,
      isRequired: true,
      hasError: _.isEmpty,
      disableTitle: true
    }));
  }, [availablePrivileges]);

  const internalPrivileges = useMemo(() => {

    return _.merge({}, defaultPrivileges, privileges);
  }, [privileges]);

  const updatePrivileges = useCallback((data) => {
    setPrivileges(data);
  }, [setPrivileges]);

  return (
    <ModelsContainer models={models} data={internalPrivileges} setData={updatePrivileges}/>
  );
};
/**
 * Page for create/edit child organization. If orgId is not provided, it will create new organization.
 * If orgId is provided, it will edit existing organization.
 * Gross list of permissions are fetched from current Organization as the child cannot
 * have more permissions than parent.
 *
 */
const AdminOrgEditPage = ({ breadcrumbs }) => {
  const { orgId } = useParams();
  // const location = useLocation();
  const api = useAPI();
  const [alerts, addErrorAlert] = useAlerts();
  const T = useTranslation();
  const queryClient = useQueryClient();
  const [organization, setOrganization] = useState(defaultOrganization);
  const { users, isLoading: isLoadingUsers } = useOrgUserRelations(orgId);
  const { groups, isLoading: isLoadingGroups } = useOrgGroupRelations(orgId);
  const [isDefaultContactUsersOpen, showDefaultContactUsersModal, hideDefaultContactsUersModal] = useBoolean(false);
  const [isDefaultContactGroupsOpen, showDefaultContactGroupsModal, hideDefaultContactsGroupsModal] = useBoolean(false);

  const model = useMemo(() => {
    return [
      {
        type: ModelType.TEXT,
        label: 'Parent',
        path: 'parent',
        disabled: true
      },
      {
        type: ModelType.TEXT,
        label: T.common.displayname,
        path: 'displayName',
        isRequired: true,
        hasError: _.isEmpty
      },
      {
        type: ModelType.TEXTAREA,
        label: T.common.description,
        path: 'description'
      },
      {
        type: ModelType.SWITCH,
        label: 'need license accept',
        path: 'settings.needLicenseAccept',
        onlySwitch: true
      }
    ];
  }, [T.common.description, T.common.displayname]);

  const tenantQuery = useQuery({
    queryKey: orgKeys.tenant(createTenantPrivilages()),
    queryFn: createQueryRequest(api.core.findRelationships),
    enabled: !!orgId
  });

  const orgQuery = useQuery({
    queryKey: orgKeys.organization(orgId),
    queryFn: createQueryRequest(api.core.getOrganization),
    enabled: !!orgId
  });

  const orgSettingsQuery = useQuery({
    queryKey: orgKeys.orgSettings(orgId),
    queryFn: createQueryRequest(api.core.getOrganizationSettings),
    enabled: !!orgId
  });

  const tenantPrivileges = useMemo(() => {
    const data = tenantQuery.data ?? [];
    const privileges = _.map(data, (priv) => _.first(_.split(priv.relation, '_privilegee')));
    return privileges;
  }, [tenantQuery.data]);

  const originalOrganization = useMemo(() => {
    const data = orgQuery.data ?? {};
    const privileges = _.reduce(data.privileges, (acc, priv) => {
      acc[priv] = true;
      return acc;
    }, {});
    const settings = orgSettingsQuery.data ?? {};
    setOrganization({ ..._.cloneDeep(data), privileges: _.cloneDeep(privileges), settings: _.cloneDeep(settings) });
    return { ...data, privileges: privileges, settings: settings };
  }, [orgQuery.data, orgSettingsQuery.data]);

  const parentOrgQuery = useQuery(orgKeys.organization(originalOrganization?.parentOrganizationId), createQueryRequest(api.core.getOrganization),
    {
      enabled: !!originalOrganization?.parentOrganizationId
    }
  );

  const parentOrg = useMemo(() => {
    const data = parentOrgQuery.data ?? {};
    return { ...data };
  }, [parentOrgQuery.data]);

  const updateOrganization = useMutation({
    mutationFn: createMutationRequest(api.core.updateOrganization),
    onMutate: (org) => {
      const oldData = queryClient.getQueryData(orgKeys.organization(orgId));
      queryClient.setQueryData(orgKeys.organization(orgId), old => {
        return { ...old, ...org };
      });
      return { org, oldData };
    },
    onSuccess: useCallback(() => {
      orgQuery.refetch();
    }, [orgQuery]),
    onError: useCallback((error, unusedVariables, context) => {
      addErrorAlert(error.message);
      console.error(error);
      queryClient.setQueryData(orgKeys.organization(orgId), context?.oldData);
      orgQuery.refetch();
    }, [addErrorAlert, queryClient, orgId, orgQuery])
  });

  const updateOrgSettings = useMutation({
    mutationFn: createMutationRequest(api.core.updateOrganizationSettings),
    onMutate: (settings) => {
      const oldData = queryClient.getQueryData(orgKeys.orgSettings(orgId));
      queryClient.setQueryData(orgKeys.orgSettings(orgId), old => {
        return { ...old, ...settings };
      });
      return { settings, oldData };
    },
    onSuccess: useCallback(() => {
      orgSettingsQuery.refetch();
    }, [orgSettingsQuery]),
    onError: useCallback((error, unusedVariables, context) => {
      addErrorAlert(error.message);
      console.error(error);
      queryClient.setQueryData(orgKeys.orgSettings(orgId), context?.oldData);
      orgSettingsQuery.refetch();
    }, [addErrorAlert, queryClient, orgId, orgSettingsQuery])
  });

  const onSave = useCallback(() => {
    const privileges = _.compact(_.map(organization.privileges, (priv, key) => {
      return priv ? key : null;
    }));
    const updatedOrg = { ..._.pick(organization, ['id', 'displayName', 'description', 'contacts']), privileges };
    updateOrganization.mutate(updatedOrg);
    updateOrgSettings.mutate({ ...organization.settings, orgId: organization.id });
  }, [organization, updateOrgSettings, updateOrganization]);

  const contentToolbar = useMemo(() => {
    return (
      <Toolbar>
        <ToolbarContent alignment="alignLeft">
          <ToolbarGroup variant="button-group">
            <ToolbarItem>
              <Button variant="primary" isDisabled={_.isEqual(originalOrganization, organization)} onClick={onSave} isLoading={updateOrganization.isLoading}>
                { T.common.save }
              </Button>
            </ToolbarItem>
          </ToolbarGroup>
        </ToolbarContent>
      </Toolbar>
    );
  }, [T.common.save, onSave, organization, originalOrganization, updateOrganization.isLoading]);

  const isFetching = (orgId) ? orgQuery.isLoading : false;

  const { availableUsers, selectedUsers } = useMemo(() => {
    const selectedUsers = _.filter(users, (user) => _.includes(organization?.contacts?.notifications?.users, user.id));
    return {
      availableUsers: users,
      selectedUsers: selectedUsers ?? []
    };
  }, [users, organization]);

  const { availableGroups, selectedGroups } = useMemo(() => {
    const selectedGroups = _.filter(groups, (group) => _.includes(organization?.contacts?.notifications?.userGroups, group.id));
    return {
      availableGroups: groups,
      selectedGroups: selectedGroups ?? []
    };
  }, [groups, organization]);

  const onSelectedUsers = useCallback((selectedRows) => {
    const users = _.map(selectedRows, row => row.id);
    setOrganization(old => {
      return { ...old, contacts: { notifications: { userGroups: old?.contacts?.notifications?.userGroups, users } } };
    });
  }, [setOrganization]);

  const onSelectedGroups = useCallback((selectedRows) => {
    const userGroups = _.map(selectedRows, row => row.id);
    setOrganization(old => {
      return { ...old, contacts: { notifications: { users: old?.contacts?.notifications?.users, userGroups } } };
    });
  }, [setOrganization]);

  return (
    <ContentPage breadcrumb={breadcrumbs} toolbar={contentToolbar} help="admin/organizations/edit">
      { alerts }
      <Flex>
        <FlexItem className="pf-u-w-33-on-xl pf-u-w-50-on-lg pf-u-w-75-on-md pf-u-w-100-on-sm">
          <ModelsContainer models={model} data={{ ...originalOrganization, ...organization, parent: parentOrg?.displayName }} setData={setOrganization} isLoading={isFetching}/>
        </FlexItem>
        <FlexItem >
          <Privileges availablePrivileges={parentOrg?.privileges || tenantPrivileges} privileges={organization?.privileges ?? []} setPrivileges={(data) => setOrganization({ ...organization, privileges: data })}/>
        </FlexItem>
        <FlexItem>
          <RelationLinks relations={relations} />
        </FlexItem>
        <FlexItem>
          <FormGroup label="Notifications">
            <Stack hasGutter>
              <StackItem>
                <Button style={{ width: '100%' }} isLoading={isLoadingUsers} variant="secondary" onClick={() => showDefaultContactUsersModal()}>{ T.admin.notifications.defaultUsers }</Button>
              </StackItem>
              <StackItem>
                <Button style={{ width: '100%' }} isLoading={isLoadingGroups} variant="secondary" onClick={() => showDefaultContactGroupsModal()}>{ T.admin.notifications.defaultGroups }</Button>
              </StackItem>
            </Stack>
          </FormGroup>
        </FlexItem>
      </Flex>
      { isDefaultContactUsersOpen && <ViewDefaultContactModal
        title={T.common.add}
        tableColumns={defaultTableColumns}
        isOpen={isDefaultContactUsersOpen}
        onClose={hideDefaultContactsUersModal}
        available={availableUsers}
        selected={selectedUsers}
        onSelected={onSelectedUsers}
      /> }
      { isDefaultContactGroupsOpen && <ViewDefaultContactModal
        title={T.common.add}
        tableColumns={defaultTableColumns}
        isOpen={isDefaultContactGroupsOpen}
        onClose={hideDefaultContactsGroupsModal}
        available={availableGroups}
        selected={selectedGroups}
        onSelected={onSelectedGroups}
      /> }
    </ContentPage>
  );
};

export default AdminOrgEditPage;
